/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { useReducer, useEffect } from 'react';
import useIAMTagsAuthz from '../../../../DataHooks/useIAMTagsAuthz/index.js';
import useTags from '../../../../DataHooks/useTags/index.js';
import useTagsResource from '../../../../DataHooks/useTagsResource/index.js';
import { getFetchOptions } from '../../../../Utilities/tagging/tagging.js';

var _excluded = ["type"];
var initialState = {
  state: 'INITIAL_MOUNT',
  userTags: [],
  accessTags: [],
  errorMessage: '',
  resource: {},
  accessTagPermissions: {
    isCreateAccessTagEnabled: false,
    isAttachAccessTagEnabled: false,
    isDetachAccessTagEnabled: false
  }
};

/**
 * Updates the state of the Resource Tag Modal component.
 * @param {*} lastState The previous state of the reducer.
 * @param {*} action The action that will be fired off.
 */
var tagsReducer = function tagsReducer(prevState, _ref) {
  var type = _ref.type,
    rest = _objectWithoutProperties(_ref, _excluded);
  switch (type) {
    // Reset everything when the modal closes.
    case 'RESET':
      return _objectSpread2({}, initialState);
    // Reset to initial state as they've retriggered the modal.
    case 'LOADING':
      return _objectSpread2(_objectSpread2({}, initialState), {}, {
        state: type
      });
    // If both user tags and resource tags are fetched, set state to TAGS_LOADED, otherwise wait for the other fetch to complete.
    case 'FETCH_USER_TAGS_COMPLETED':
      {
        var newState = type;
        if (prevState.state === 'FETCH_RESOURCE_TAGS_COMPLETED') {
          newState = 'TAGS_LOADED';
        }
        return _objectSpread2(_objectSpread2({}, prevState), {}, {
          state: newState
        }, rest);
      }
    // If both user tags and resource tags are fetched, set state to TAGS_LOADED, otherwise wait for the other fetch to complete.
    case 'FETCH_RESOURCE_TAGS_COMPLETED':
      {
        var _newState = type;
        if (prevState.state === 'FETCH_USER_TAGS_COMPLETED') {
          _newState = 'TAGS_LOADED';
        }
        return _objectSpread2(_objectSpread2({}, prevState), {}, {
          state: _newState
        }, rest);
      }
    default:
      return prevState;
  }
};
var useTagsState = function useTagsState(accountId, mock, open, resourceId, supportAccessTags, t) {
  // Component state
  var _useReducer = useReducer(tagsReducer, initialState),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    tagsState = _useReducer2[0],
    dispatch = _useReducer2[1];
  // Constants
  var options = getFetchOptions(accountId, mock, open, resourceId, supportAccessTags);
  var defaultErrorMessage = t('Error fetching tags');
  // Data Hooks
  var _useIAMTagsAuthz = useIAMTagsAuthz(options.auth()),
    refetchAuth = _useIAMTagsAuthz.refetch,
    authData = _useIAMTagsAuthz.data;
  var _useTags = useTags(options.userTags()),
    refetchUserTags = _useTags.refetch,
    userTagsData = _useTags.data;
  var _useTags2 = useTags(options.accessTags()),
    refetchAccessTags = _useTags2.refetch,
    accessTagsData = _useTags2.data;
  var _useTagsResource = useTagsResource(options.resourceTags()),
    refetchResourceTags = _useTagsResource.refetch,
    resourceTagsData = _useTagsResource.data,
    resourceTagsError = _useTagsResource.error;

  // Upon opening and closing the modal reset the state or refetch the tags.
  useEffect(function () {
    // Need this check in order to not fetch twice / render the reset state on initial mount.
    if (!open && tagsState.state !== 'INITIAL_MOUNT') {
      dispatch({
        type: 'RESET'
      });
    }
    if (open && tagsState.state === 'INITIAL_MOUNT') {
      dispatch({
        type: 'LOADING'
      });
      refetchAuth();
      refetchAccessTags();
      refetchUserTags();
      refetchResourceTags();
    }
    // Disable the next line because watching these deps will trigger an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mock]);

  // Upon success update the state of the component.
  useEffect(function () {
    // LOADING check is needed for resource list as it never sets open=false and therefore never triggers RESET
    // FETCH_USER_TAGS_COMPLETED check is to ensure we set access & resource tags data even if user tags are already loaded
    if ((accountId && authData || !accountId) && accessTagsData && resourceTagsData && (tagsState.state === 'LOADING' || tagsState.state === 'FETCH_USER_TAGS_COMPLETED')) {
      dispatch({
        type: 'FETCH_RESOURCE_TAGS_COMPLETED',
        accessTags: accessTagsData.tags,
        accessTagPermissions: {
          isCreateAccessTagEnabled: authData ? authData.isCreateAccessTagEnabled : true,
          // fallback to true for cases where accountId isn't available so the tag area is enabled
          isAttachAccessTagEnabled: authData ? authData.isAttachAccessTagEnabled : true,
          isDetachAccessTagEnabled: authData ? authData.isDetachAccessTagEnabled : true
        },
        userTagPermissions: {
          isAttachUserTagEnabled: authData ? authData.isAttachUserTagEnabled : true,
          isDetachUserTagEnabled: authData ? authData.isDetachUserTagEnabled : true
        },
        resource: _objectSpread2({
          type: '',
          resourceId: resourceId,
          tags: [],
          access_tags: []
        }, resourceTagsData)
      });
    }

    // LOADING check is needed for resource list as it never sets open=false and therefore never triggers RESET
    // FETCH_USER_TAGS_COMPLETED check is to ensure we set resource data even if user tags are already loaded
    if (!supportAccessTags && (accountId && authData || !accountId) && resourceTagsData && (tagsState.state === 'LOADING' || tagsState.state === 'FETCH_USER_TAGS_COMPLETED')) {
      dispatch({
        type: 'FETCH_RESOURCE_TAGS_COMPLETED',
        userTagPermissions: {
          isAttachUserTagEnabled: authData ? authData.isAttachUserTagEnabled : true,
          // fallback to true for cases where accountId isn't available so the tag area is enabled
          isDetachUserTagEnabled: authData ? authData.isDetachUserTagEnabled : true
        },
        resource: _objectSpread2({
          type: '',
          resourceId: resourceId,
          tags: [],
          access_tags: []
        }, resourceTagsData)
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, authData, accessTagsData, resourceTagsData, supportAccessTags]);

  // Handle user tags differently in order to gracefully render in case of failure.
  useEffect(function () {
    if (userTagsData && (tagsState.state === 'LOADING' ||
    // LOADING check is needed for resource list as it never sets open=false and therefore never triggers RESET
    tagsState.state === 'FETCH_RESOURCE_TAGS_COMPLETED') // set user tag data even if resource data & access tags (if they are supported) are already loaded
    ) {
      dispatch({
        type: 'FETCH_USER_TAGS_COMPLETED',
        userTags: userTagsData.tags
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTagsData]);

  // Watch for any errors and dispatch an error message.
  useEffect(function () {
    if (resourceTagsError) {
      dispatch({
        type: 'FETCH_RESOURCE_TAGS_COMPLETED',
        errorMessage: resourceTagsError.message || defaultErrorMessage
      });
    }
  }, [defaultErrorMessage, resourceTagsError]);
  return _objectSpread2(_objectSpread2({}, tagsState), {}, {
    dispatch: dispatch
  });
};
var useTagsState$1 = useTagsState;

export { useTagsState$1 as default };
